import { Game } from "./game";
import type { Faction } from "./data/types";
import { getFaction, getDef } from "./data";
import { drawSprite, paletteFor } from "./render/art";
import type { Difficulty } from "./sim/ai";
import { audio, bindAudioUnlock } from "./render/audio";
import { toggleSettings } from "./ui/settingsui";

// Match flow: faction-select screen -> game -> end screen -> new match.
// All screens share one visual language: an animated battlefield-void backdrop,
// large faction crests, and smooth fade transitions.

const MENU_CSS = `
#menu { position:fixed; inset:0; z-index:40; display:flex; flex-direction:column; align-items:center; justify-content:center;
  font-family:"Segoe UI","Helvetica Neue",Arial,sans-serif; color:#dfe8ef; overflow:hidden; }
#menubg { position:absolute; inset:0; z-index:-1; display:block; }
#menu .wrap { position:relative; display:flex; flex-direction:column; align-items:center; }
#menu h1 { font-size:72px; letter-spacing:18px; margin:0; color:#eaf6ff; font-weight:700;
  text-shadow:0 0 30px #1d6f8a, 0 0 60px #0b3a4a; padding-left:18px; }
#menu .tag { color:#8299ab; margin:8px 0 40px; letter-spacing:4px; font-size:12px; }
#menu .cards { display:flex; gap:28px; }
.fcard { width:250px; background:linear-gradient(180deg,#0d1720d9,#0a1119cc); border:1px solid #24333f; border-radius:14px;
  padding:20px 18px 20px; cursor:pointer; text-align:center; transition:transform .18s, border-color .18s, box-shadow .18s;
  backdrop-filter:blur(3px); }
.fcard:hover { transform:translateY(-6px); border-color:#3a5666; }
.fcard.sel { border-color:var(--c); box-shadow:0 0 34px -6px var(--c), inset 0 0 0 1px var(--c); background:#0f1c26; }
.fcard .crest { width:130px; height:130px; margin:0 auto 6px; }
.fcard .role { color:var(--c); font-size:10px; letter-spacing:3px; text-transform:uppercase; margin-top:4px; }
.fcard h2 { margin:6px 0 6px; letter-spacing:3px; font-size:24px; }
.fcard p { color:#9fb0bd; font-size:12px; line-height:1.6; min-height:76px; margin:8px 0 0; }
#diff { margin:34px 0 26px; display:flex; gap:10px; align-items:center; }
#diff .lbl { color:#8299ab; font-size:11px; letter-spacing:2px; margin-right:4px; }
#diff button { background:#0e151bcc; color:#9fb0bd; border:1px solid #26343f; padding:8px 20px; border-radius:6px;
  cursor:pointer; letter-spacing:1.5px; font-size:12px; transition:.12s; }
#diff button:hover { border-color:#3a5666; color:#cdd9e2; }
#diff button.sel { background:#153341; color:#e8f4ff; border-color:#40e0ff; box-shadow:0 0 16px -6px #40e0ff; }
#startbtn { background:linear-gradient(180deg,#123244,#0d2634); color:#eaf6ff; border:1px solid #40e0ff; padding:15px 62px;
  font-size:18px; border-radius:8px; cursor:pointer; letter-spacing:5px; font-weight:600; transition:.15s;
  box-shadow:0 0 0 0 #40e0ff44; }
#startbtn:hover { background:linear-gradient(180deg,#184356,#123244); box-shadow:0 0 28px -6px #40e0ff, inset 0 0 0 1px #40e0ff66; transform:translateY(-2px); }
#menu .gear { position:absolute; top:20px; right:22px; }

#fade { position:fixed; inset:0; z-index:200; background:#04060a; pointer-events:none; opacity:0;
  transition:opacity .32s ease; }
#fade.on { opacity:1; pointer-events:auto; }

#endscreen { position:fixed; inset:0; z-index:60; display:flex; flex-direction:column; align-items:center; justify-content:center;
  font-family:"Segoe UI",Arial,sans-serif; overflow:hidden; }
#endscreen .crest { width:160px; height:160px; margin-bottom:14px; }
#endscreen h1 { font-size:76px; letter-spacing:12px; margin:0 0 4px; font-weight:700; }
#endscreen p { color:#9fb0bd; margin:0 0 30px; letter-spacing:1px; }
#endscreen button { background:linear-gradient(180deg,#123244,#0d2634); color:#eaf6ff; border:1px solid #40e0ff;
  padding:13px 44px; font-size:15px; border-radius:8px; cursor:pointer; letter-spacing:3px; transition:.15s; }
#endscreen button:hover { box-shadow:0 0 24px -6px #40e0ff; transform:translateY(-2px); }
`;

const FACTION_BLURB: Record<Faction, string> = {
  vantage: "Refinement. Angular hulls, precise beams, longer range and the strongest static defence. Wins fair fights, loses to swarms.",
  feral: "Proliferation. Rounded hulls, ballistic arcs and splash. Cheaper, faster, more numerous. Raids, flanks and drowns you in metal.",
  neutral: "",
};

let game: Game | null = null;
let bgStop: (() => void) | null = null;

function ensureStyle(): void {
  if (document.getElementById("menu-style")) return;
  const st = document.createElement("style");
  st.id = "menu-style";
  st.textContent = MENU_CSS;
  document.head.appendChild(st);
}

function fadeEl(): HTMLDivElement {
  let f = document.getElementById("fade") as HTMLDivElement | null;
  if (!f) { f = document.createElement("div"); f.id = "fade"; document.body.appendChild(f); }
  return f;
}

/** Fade to black, run `mid`, then fade back in. */
function transition(mid: () => void): void {
  const f = fadeEl();
  f.classList.add("on");
  setTimeout(() => {
    mid();
    // next frame, fade back
    requestAnimationFrame(() => requestAnimationFrame(() => f.classList.remove("on")));
  }, 330);
}

// --- Shared animated backdrop: drifting embers + parallax grid + faction crests.
function startBackdrop(canvas: HTMLCanvasElement, tintA: string, tintB: string): () => void {
  const g = canvas.getContext("2d")!;
  let raf = 0;
  let running = true;
  const embers: { x: number; y: number; vx: number; vy: number; s: number; a: number; c: string }[] = [];
  const resize = () => {
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
  };
  resize();
  window.addEventListener("resize", resize);
  for (let i = 0; i < 70; i++) {
    embers.push({
      x: Math.random() * canvas.width, y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 6, vy: -4 - Math.random() * 10,
      s: 0.6 + Math.random() * 2, a: 0.2 + Math.random() * 0.5,
      c: Math.random() < 0.5 ? tintA : tintB,
    });
  }
  let t = 0;
  let last = performance.now();
  const frame = (now: number) => {
    if (!running) return;
    const dt = Math.min(0.05, (now - last) / 1000); last = now; t += dt;
    const W = canvas.width, H = canvas.height;
    const rg = g.createRadialGradient(W / 2, H * 0.42, 0, W / 2, H * 0.42, Math.max(W, H) * 0.7);
    rg.addColorStop(0, "#0b131c"); rg.addColorStop(1, "#04060a");
    g.fillStyle = rg; g.fillRect(0, 0, W, H);
    // Parallax grid.
    g.strokeStyle = "rgba(90,120,150,0.05)"; g.lineWidth = 1;
    const step = 60; const off = (t * 8) % step;
    g.beginPath();
    for (let x = -off; x < W; x += step) { g.moveTo(x, 0); g.lineTo(x, H); }
    for (let y = -off; y < H; y += step) { g.moveTo(0, y); g.lineTo(W, y); }
    g.stroke();
    // Drifting embers.
    for (const e of embers) {
      e.x += e.vx * dt; e.y += e.vy * dt;
      if (e.y < -10) { e.y = H + 10; e.x = Math.random() * W; }
      if (e.x < -10) e.x = W + 10; if (e.x > W + 10) e.x = -10;
      g.globalAlpha = e.a * (0.6 + 0.4 * Math.sin(t * 2 + e.x));
      g.fillStyle = e.c;
      g.fillRect(e.x, e.y, e.s, e.s);
    }
    g.globalAlpha = 1;
    raf = requestAnimationFrame(frame);
  };
  raf = requestAnimationFrame(frame);
  return () => { running = false; cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
}

function drawCrest(cv: HTMLCanvasElement, faction: Faction, sizePx: number): void {
  const g = cv.getContext("2d")!;
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  cv.width = sizePx * dpr; cv.height = sizePx * dpr;
  cv.style.width = sizePx + "px"; cv.style.height = sizePx + "px";
  g.scale(dpr, dpr);
  const fd = getFaction(faction);
  const c = sizePx / 2;
  g.translate(c, c);
  // Hex crest frame.
  g.strokeStyle = fd.color; g.globalAlpha = 0.7; g.lineWidth = 2;
  g.beginPath();
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 - Math.PI / 2;
    const r = sizePx * 0.44;
    const x = Math.cos(a) * r, y = Math.sin(a) * r;
    if (i === 0) g.moveTo(x, y); else g.lineTo(x, y);
  }
  g.closePath(); g.stroke();
  g.globalAlpha = 0.16;
  g.fillStyle = fd.color; g.fill();
  g.globalAlpha = 1;
  // Commander sprite, large.
  g.save();
  const cdef = getDef(fd.commanderId);
  const scale = (sizePx * 0.34) / cdef.radius / 2;
  g.scale(scale, scale);
  g.rotate(-Math.PI / 2);
  drawSprite(g, "commander", faction, cdef.radius, paletteFor(faction));
  g.restore();
}

function showMenu(): void {
  ensureStyle();
  bindAudioUnlock();
  const app = document.getElementById("app")!;
  const menu = document.createElement("div");
  menu.id = "menu";
  menu.innerHTML = `
    <canvas id="menubg"></canvas>
    <div class="iconbtn panel gear" id="menugear" style="pointer-events:auto;width:38px;height:38px;display:flex;align-items:center;justify-content:center;background:#0e1720cc;border:1px solid #1e2c37;border-radius:8px;cursor:pointer;color:#c5d2dc">
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M19.1 4.9L17 7M7 17l-2.1 2.1"/></svg>
    </div>
    <div class="wrap">
      <h1>TORRENT</h1>
      <div class="tag">STREAMING-ECONOMY MASS RTS · 1v1 vs AI</div>
      <div class="cards"></div>
      <div id="diff">
        <span class="lbl">ENEMY AI</span>
        <button data-d="easy">EASY</button>
        <button data-d="normal" class="sel">NORMAL</button>
        <button data-d="hard">HARD</button>
      </div>
      <button id="startbtn">DEPLOY</button>
    </div>
  `;
  app.appendChild(menu);

  bgStop?.();
  bgStop = startBackdrop(menu.querySelector("#menubg") as HTMLCanvasElement, "#40e0ff", "#ff5a3c");
  document.getElementById("boot")?.remove();

  (menu.querySelector("#menugear") as HTMLElement).onclick = () => toggleSettings(22, 66);

  const cards = menu.querySelector(".cards") as HTMLDivElement;
  let chosen: Faction = "vantage";
  let diff: Difficulty = "normal";

  const factions: Faction[] = ["vantage", "feral"];
  for (const f of factions) {
    const fd = getFaction(f);
    const card = document.createElement("div");
    card.className = "fcard" + (f === chosen ? " sel" : "");
    card.style.setProperty("--c", fd.color);
    const cv = document.createElement("canvas");
    cv.className = "crest";
    drawCrest(cv, f, 130);
    card.appendChild(cv);
    card.insertAdjacentHTML("beforeend",
      `<div class="role">${f === "vantage" ? "beams · range" : "swarm · splash"}</div>
       <h2 style="color:${fd.color}">${fd.name}</h2>
       <p>${FACTION_BLURB[f]}</p>`);
    card.onclick = () => {
      chosen = f;
      audio.ui("select");
      for (const c of Array.from(cards.children)) c.classList.remove("sel");
      card.classList.add("sel");
    };
    cards.appendChild(card);
  }

  for (const b of Array.from(menu.querySelectorAll("#diff button")) as HTMLButtonElement[]) {
    b.onclick = () => {
      diff = b.dataset.d as Difficulty;
      audio.ui("click");
      for (const x of Array.from(menu.querySelectorAll("#diff button"))) x.classList.remove("sel");
      b.classList.add("sel");
    };
  }

  (menu.querySelector("#startbtn") as HTMLButtonElement).onclick = () => {
    audio.unlock();
    audio.ui("place");
    transition(() => {
      menu.remove();
      bgStop?.(); bgStop = null;
      const enemy: Faction = chosen === "vantage" ? "feral" : "vantage";
      startGame(chosen, enemy, diff);
    });
  };
}

function startGame(player: Faction, enemy: Faction, diff: Difficulty): void {
  const seed = (Date.now() ^ (Math.random() * 0xffffffff)) >>> 0;
  game = new Game(seed, { playerFaction: player, enemyFaction: enemy, difficulty: diff });
  (window as unknown as { __t: Game }).__t = game; // debug handle
  (window as unknown as { __perf: () => unknown }).__perf = () => game?.perfStats(); // perf probe
  game.onEnd = (winner) => showEndScreen(winner, player);
  game.start();
}

function showEndScreen(winner: number, player: Faction): void {
  ensureStyle();
  const app = document.getElementById("app")!;
  const win = winner === 1;
  const end = document.createElement("div");
  end.id = "endscreen";
  end.style.opacity = "0";
  end.style.transition = "opacity .5s ease";
  end.innerHTML = `
    <canvas id="endbg" style="position:absolute;inset:0;z-index:-1"></canvas>
    <canvas class="crest"></canvas>
    <h1 style="color:${win ? "#7ce6a0" : "#ff5a3c"};text-shadow:0 0 34px ${win ? "#2f8f5a" : "#8a2a1c"}">${win ? "VICTORY" : "DEFEAT"}</h1>
    <p>${win ? "Enemy Commander destroyed. Herest is yours." : "Your Commander was destroyed."}</p>
    <button id="again">NEW MATCH</button>
  `;
  app.appendChild(end);
  const stop = startBackdrop(end.querySelector("#endbg") as HTMLCanvasElement, win ? "#7ce6a0" : "#ff5a3c", "#40e0ff");
  drawCrest(end.querySelector(".crest") as HTMLCanvasElement, player, 160);
  requestAnimationFrame(() => { end.style.opacity = "1"; });

  (end.querySelector("#again") as HTMLButtonElement).onclick = () => {
    audio.ui("click");
    transition(() => {
      end.remove();
      stop();
      game?.destroy();
      game = null;
      showMenu();
    });
  };
}

showMenu();
